
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface Job {
  id: string;
  badge: { text: string; color: string } | null;
  position: string;
  company: string;
  location: string;
  experience: string;
  salary: string;
  workType: string;
  category: string;
  image: string;
  views: number;
  applicants: number;
  postedDate: string;
  isHot: boolean;
}

interface JobCardProps {
  job: Job;
  index: number;
}

export default function JobCard({ job, index }: JobCardProps) {
  const navigate = useNavigate();

  const daysAgo = () => {
    const posted = new Date(job.postedDate);
    const now = new Date();
    const diff = Math.floor((now.getTime() - posted.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return '오늘';
    if (diff === 1) return '어제';
    return `${diff}일 전`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="group cursor-pointer"
      data-product-shop
      onClick={() => navigate(`/job/${job.id}`)}
    >
      <div className="h-full bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:border-neon-purple/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-glow overflow-hidden">
        {/* Image */}
        <div className="relative w-full h-44 overflow-hidden">
          <img
            src={job.image}
            alt={job.position}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-dark-deep/80 via-transparent to-transparent"></div>

          {/* Badges */}
          <div className="absolute top-3 left-3 flex items-center gap-2">
            {job.badge && (
              <span className={`${job.badge.color} text-dark-deep text-[10px] font-bold px-2.5 py-1 rounded-md`}>
                {job.badge.text}
              </span>
            )}
            {job.isHot && (
              <span className="bg-red-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-md flex items-center gap-1">
                <i className="ri-fire-fill w-3 h-3 flex items-center justify-center"></i>
                HOT
              </span>
            )}
          </div>

          {/* Bookmark */}
          <button
            onClick={(e) => { e.stopPropagation(); }}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm text-white/70 hover:text-neon-pink hover:bg-black/60 transition-all cursor-pointer"
          >
            <i className="ri-bookmark-line w-4 h-4 flex items-center justify-center text-sm"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Category & Work Type */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] font-medium text-neon-purple bg-neon-purple/10 px-2 py-0.5 rounded">
              {job.category}
            </span>
            <span className="text-[10px] font-medium text-gray-500 bg-white/5 px-2 py-0.5 rounded">
              {job.workType}
            </span>
          </div>

          {/* Position */}
          <h3 className="text-base font-bold text-white mb-1.5 group-hover:text-neon-purple transition-colors line-clamp-1">
            {job.position}
          </h3>

          {/* Company */}
          <p className="text-sm text-gray-400 mb-3">{job.company}</p>

          {/* Location & Experience */}
          <div className="flex items-center gap-3 mb-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <i className="ri-map-pin-line w-3.5 h-3.5 flex items-center justify-center"></i>
              {job.location}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-briefcase-line w-3.5 h-3.5 flex items-center justify-center"></i>
              {job.experience}
            </span>
          </div>

          {/* Bottom */}
          <div className="flex items-center justify-between pt-4 border-t border-white/5">
            <span className="text-sm font-bold bg-gradient-neon bg-clip-text text-transparent">
              {job.salary}
            </span>
            <div className="flex items-center gap-3 text-[11px] text-gray-500">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
                {job.views.toLocaleString()}
              </span>
              <span>{daysAgo()}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
